import React, { useRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PatternlibButton,
  PatternlibSelectionCardGroup,
  PatternlibSelectionCard,
  PatternlibCheckOption,
  PatternlibMultiselect,
} from "@liebherr/patternlib-react";
import PropTypes from "prop-types";
import datasheetExampleDE from "../../img/datasheet_example_de.png";
import datasheetExampleEN from "../../img/datasheet_example_en.png";
import styles from "./FormatSelector.module.css";
import { pushToDataLayer } from "../../helpers/helpers";
import useMutationObserver from "../../hooks/useMutationObserver";

const FormatSelector = ({ part }) => {
  const { t, i18n } = useTranslation();

  const [options, setOptions] = useState();
  const [format, setFormat] = useState("");
  const formatRef = useRef(); //State auslesen/mitschicken
  const [downloadPDF, setDownloadPDF] = useState(true);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const formatDownloadSuccessGA = (formFormat) => {
    const eventName = "format_download_sucess";
    const pagePath = location.pathname;
    const pageType = "download_formats";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const form_format = formFormat;
    let props = {
      pagePath,
      pageType,
      language,
      form_format,
    };

    pushToDataLayer(eventName, props);
  };

  // use mutation observer to get current screen size
  const mutationObserverOptions = {
    config: { attributes: true, childList: true, subtree: true },
  };
  const target = document.documentElement;
  const attributeName = "breakpoint";
  const screen = useMutationObserver(
    mutationObserverOptions,
    target,
    attributeName
  );

  const getAllFormats = async () => {
    const response = await fetch(
      "https://webapi.partcommunity.com/cadqualifier.asp"
    );
    response
      .text()
      .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
      .then((data) => {
        const formats = data.getElementsByTagName("qualifier");
        const arr = [];

        for (let i = 0; i < formats.length; ++i) {
          const formatName = formats[i].innerHTML;
          if (formatName !== "PDFDATASHEET") {
            const obj = {
              label: formatName,
              value: formatName,
            };
            arr.push(obj);
          }
        }

        setOptions(arr);
      });
  };

  const onButtonClick = () => {
    if (!loading && part !== undefined) {
      let formatStr = format;
      if (downloadPDF) {
        formatStr = formatStr + ", PDFDATASHEET";
      }

      formatRef.current = formatStr;

      const event = new CustomEvent("hyzDownload", {
        bubbles: true,
        detail: { part, format: formatStr, language: i18n.language },
      });
      window.dispatchEvent(event);
      setLoading(true);
    }
  };

  const onSelectDropdownListener = (event) => {
    if (event.detail.selected !== undefined) {
      const selectedValue = event.detail.selected;
      const formats = [];
      selectedValue.map((f) => {
        formats.push(f.value);
      });
      const formatStr = formats.join(", ");
      setFormat(formatStr);
    } else {
      setFormat([]);
    }
  };
  const onChangeCheckboxListener = (event) => {
    setDownloadPDF(event.current.checked);
  };

  useEffect(() => {
    const onFinishDownload = (event) => {
      formatDownloadSuccessGA(formatRef.current);
      setLoading(false);
    };
    window.addEventListener("hyzFinishDownload", onFinishDownload);
    return () => {
      window.removeEventListener("hyzFinishDownload", onFinishDownload);
    };
  }, []);

  useEffect(() => {
    getAllFormats();
  }, []);

  // Sorting function to prioritize options containing "STEP" or "CREO"
  function customSort(a, b) {
    // Check if label of option 'a' contains "CREO" or "STEP"
    const includesStepA = a.label.includes("CREO") || a.label.includes("STEP");

    // Check if label of option 'b' contains "CREO" or "STEP"
    const includesStepB = b.label.includes("CREO") || b.label.includes("STEP");

    // Compare the two options based on their inclusion of "CREO" or "STEP"
    if (includesStepA && !includesStepB) {
      // If option 'a' contains "CREO" or "STEP", and option 'b' does not, place 'a' before 'b' in the sorted list
      return -1;
    } else if (!includesStepA && includesStepB) {
      // If option 'b' contains "CREO" or "STEP", and option 'a' does not, place 'b' before 'a' in the sorted list
      return 1;
    } else {
      // If both options contain "CREO" or "STEP", or both do not contain them,
      // sort them alphabetically based on their label using the localeCompare method
      return a.label.localeCompare(b.label);
    }
  }

  // Assuming your options array is named "options"
  const sortedOptions = options?.sort(customSort);

  return (
    <div className={styles.downloads}>
      <PatternlibSelectionCardGroup
        grid-wrap
        horizontal
        items-min-width={screen === "isMobile" ? "196px" : "256px"}
        name="demo"
        type="checkbox"
        value="PDFDATASHEET"
      >
        <PatternlibSelectionCard
          type="checkbox"
          value="PDFDATASHEET"
          disabled="false"
          onLhChange={(e) => onChangeCheckboxListener(e)}
        >
          <div className={styles.card}>
            <p>{t("FormatSelector.downloadPDF")}</p>
            <img
              className={styles.thumbnail}
              slot="element"
              src={
                i18n.language === "de" || i18n.language === "de-DE"
                  ? datasheetExampleDE
                  : datasheetExampleEN
              }
              alt={t("FormatSelector.PDFThumbnailAlt")}
            ></img>
          </div>
        </PatternlibSelectionCard>
      </PatternlibSelectionCardGroup>
      {sortedOptions && (
        <PatternlibMultiselect
          label={t("FormatSelector.multiselectLabel")}
          placeholder={t("FormatSelector.multiselectPlaceholder")}
          placeholderText={t("FormatSelector.multiselectPlaceholderText")}
          onLhSelect={onSelectDropdownListener}
          close-on-scroll={false}
          filterable
          select-all-text={t("FormatSelector.multiselectSelectAllText")}
          deselect-all-text={t("FormatSelector.multiselectDeselectAllText")}
        >
          {sortedOptions.map((item, index) => {
            return (
              <PatternlibCheckOption
                value={item.value}
                key={index}
                label={item.label}
              >
                {item.value}
              </PatternlibCheckOption>
            );
          })}
        </PatternlibMultiselect>
      )}
      <div
        className={styles.buttonWrapper}
        style={{
          pointerEvents:
            (format.length < 1 && !downloadPDF) || format == undefined
              ? "none"
              : "auto",
        }}
      >
        <PatternlibButton
          label={t("FormatSelector.download")}
          disabled={(format.length < 1 && !downloadPDF) || format == undefined}
          onClick={onButtonClick}
          loading={loading}
          no-states={loading}
        ></PatternlibButton>
      </div>
    </div>
  );
};

FormatSelector.propTypes = {
  part: PropTypes.object,
};

export default FormatSelector;
